import { ProductsGrid } from '@wix/package-picker-components';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { useProductsGridRefContext } from 'providers/ProductsGridContextRefProvider/ProductsGridContextRefProvider';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseGridSections } from '../../../pages/PlansPage/AdvancedPlans/PlansGrid/UseGridSections';
import { RootStore, withInjectedRootStore } from '../../../providers';
import {
  AdvancedPlansFeature,
  AdvancedPlansFeatureSection,
  AdvancedPlansProductData,
  FeaturesGroupSection,
} from '../../../types';
import FeatureGroup from './FeatureGroup';
import { FeatureRow } from './FeatureRow';
import FeatureSection from './FeatureSection';
import { GridHeader } from './GridHeader';

export interface PlansGridProps extends InjectedExperimentsProps {
  rootStore: RootStore;
  products: AdvancedPlansProductData[];
  onClickSelect(productData: AdvancedPlansProductData): void;
  features: AdvancedPlansFeature[];
  groups: FeaturesGroupSection[];
  sections: AdvancedPlansFeatureSection[];
}

const PlansGridComp: React.FC<PlansGridProps> = ({
  rootStore,
  products,
  onClickSelect,
  features,
  groups,
  sections,
}) => {
  const gridRef = React.useRef<HTMLDivElement>(null);
  const gridHeaderRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { openedSections, foldedSections } = UseGridSections(
    sections,
    rootStore.appStore,
  );
  const { setRef } = useProductsGridRefContext();
  React.useEffect(() => {
    setRef && setRef(gridRef);
  }, []);
  const {
    isJapanesePackagePicker,
    planType,
    appContext: { biLogger, siteGuid },
  } = rootStore.appStore;

  return (
    <ProductsGrid productsCount={products.length} innerRef={gridRef}>
      <GridHeader
        products={products}
        innerRef={gridHeaderRef}
        onClickSelect={onClickSelect}
      />
      {features.map((feature) => (
        <FeatureRow
          feature={feature}
          products={products}
          key={feature.translationKey}
          isJapanesePackagePicker={isJapanesePackagePicker}
        />
      ))}
      {groups?.map((group) => (
        <FeatureGroup
          group={group}
          key={group.translationKey}
          products={products}
          isJapanesePackagePicker={isJapanesePackagePicker}
        />
      ))}
      {openedSections.map((section) => (
        <FeatureSection
          key={section.translationKey}
          section={section}
          products={products}
          isJapanesePackagePicker={isJapanesePackagePicker}
          isOpen
        />
      ))}
      <ProductsGrid.Collapsible
        labels={{
          open: t('premium-purchase-plan.show_more.CTA'),
          close: t('premium-purchase-plan.show_less.CTA'),
        }}
        onToggle={(action) =>
          biLogger.logClickOnShowAllFeatures({
            ppType: planType,
            siteGuid,
            clickType: action === 'close' ? 'hide' : 'show',
          })
        }
      >
        {foldedSections.map((section) => (
          <FeatureSection
            key={section.translationKey}
            section={section}
            products={products}
            isJapanesePackagePicker={isJapanesePackagePicker}
          />
        ))}
      </ProductsGrid.Collapsible>
    </ProductsGrid>
  );
};

export const PlansGrid = withExperiments(withInjectedRootStore(PlansGridComp));
